/**
 * 🎯 FEATURE FLAGS SYSTEM
 * Epic 8 Implementation - Progressive Capability Disclosure
 * 
 * Manages progressive feature unlocking tied to epic completion
 * Supports Epic 7.5 baseline through Epic 16 evolution
 */

import type { FeatureFlags as FeatureFlagsType, EPIC_FEATURE_FLAGS as EpicFeatureFlagsType } from '../types/walkthrough';
import { EPIC_FEATURE_FLAGS } from '../types/walkthrough';

// Current Epic Progress - Update this as epics are completed
const CURRENT_EPIC = 7.5;

// Default Feature Flags (Epic 7.5 Baseline)
const DEFAULT_FEATURE_FLAGS: FeatureFlagsType = {
  // Epic 7.5 Baseline Features (Always Enabled)
  'dashboard-navigation': true,
  'basic-agent-access': true,
  'brand-intelligence': true,
  
  // Epic 8+ Features (Progressively Enabled)
  'agent-activation': false,
  'workflow-basics': false,
  'demo-mode': false,
  
  // Epic 9+ Features
  'workflow-canvas': false,
  'agent-collaboration': false,
  'multi-agent-handoffs': false,
  
  // Epic 10+ Features
  'ecosystem-navigator': false,
  'emma-orchestrator': false,
  'command-center': false,
  
  // Epic 11+ Features
  'triad-orchestration': false,
  'customer-vendor-partner': false,
  'emma-coordination': false,
  
  // Epic 12-16 Features
  'advanced-analytics': false,
  'ai-recommendations': false,
  'enterprise-integration': false,
  'marketplace-access': false,
  'global-orchestration': false,
  
  // Emma Backend Session Persistence (Epic 12+)
  // Enables cross-device/session resume for Emma conversations
  'emma.backendSession': true,
  
  // Epic 17 Features (HIDDEN - Backend Not Implemented)
  // 🔴 CRITICAL: Epic 17 frontend exists but backend missing
  // Feature flagged OFF until backend implementation complete
  'epic17-auto-actions': false,
  'epic17-setup-orchestrator': false,
  'epic17-profile-builder': false,
  'epic17-market-intelligence': false,
  'epic17-persona-generator': false,
  'epic17-campaign-architect': false,
  'epic17-problem-solver': false,
  'epic17-content-orchestrator': false,
  'epic17-quality-optimizer': false,
  'epic17-performance-tuner': false,
};

// Local Storage Keys
const FEATURE_FLAGS_STORAGE_KEY = 'crowsnest-feature-flags';
const EPIC_PROGRESS_STORAGE_KEY = 'crowsnest-epic-progress';

/**
 * Load feature flags from local storage with epic-based defaults
 */
export function loadFeatureFlags(): FeatureFlagsType {
  try {
    const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    const epicProgress = getEpicProgress();
    
    if (stored) {
      const parsedFlags = JSON.parse(stored);
      // Merge with epic-based enablement
      return {
        ...DEFAULT_FEATURE_FLAGS,
        ...getEpicBasedFlags(epicProgress),
        ...parsedFlags
      };
    }
    
    // Return epic-based flags if no stored preferences
    return {
      ...DEFAULT_FEATURE_FLAGS,
      ...getEpicBasedFlags(epicProgress)
    };
  } catch (error) {
    console.warn('Failed to load feature flags from storage:', error);
    return {
      ...DEFAULT_FEATURE_FLAGS,
      ...getEpicBasedFlags(getEpicProgress())
    };
  }
}

/**
 * Save feature flags to local storage
 */
export function saveFeatureFlags(flags: FeatureFlagsType): void {
  try {
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    console.warn('Failed to save feature flags to storage:', error);
  }
}

/**
 * Get current epic progress
 */
export function getEpicProgress(): number {
  try {
    const stored = localStorage.getItem(EPIC_PROGRESS_STORAGE_KEY);
    return stored ? parseFloat(stored) : CURRENT_EPIC;
  } catch {
    return CURRENT_EPIC;
  }
}

/**
 * Update epic progress and enable corresponding features
 */
export function updateEpicProgress(epic: number): void {
  try {
    localStorage.setItem(EPIC_PROGRESS_STORAGE_KEY, epic.toString());
    
    // Auto-enable features for completed epic
    const currentFlags = loadFeatureFlags();
    const newFlags = {
      ...currentFlags,
      ...getEpicBasedFlags(epic)
    };
    saveFeatureFlags(newFlags);
  } catch (error) {
    console.warn('Failed to update epic progress:', error);
  }
}

/**
 * Get feature flags that should be enabled based on epic progress
 */
function getEpicBasedFlags(currentEpic: number): Partial<FeatureFlagsType> {
  let enabledFlags: Partial<FeatureFlagsType> = {};
  
  // Enable features for all completed epics
  Object.entries(EPIC_FEATURE_FLAGS).forEach(([epic, flags]) => {
    const epicNumber = parseFloat(epic);
    if (currentEpic >= epicNumber) {
      enabledFlags = { ...enabledFlags, ...flags };
    }
  });
  
  return enabledFlags;
}

/**
 * Check if a specific feature is enabled
 */
export function isFeatureEnabled(flag: keyof FeatureFlagsType, flags?: FeatureFlagsType): boolean {
  const currentFlags = flags || loadFeatureFlags();
  return currentFlags[flag] === true;
}

/**
 * Enable a specific feature flag
 */
export function enableFeature(flag: keyof FeatureFlagsType): void {
  const currentFlags = loadFeatureFlags();
  const updatedFlags = { ...currentFlags, [flag]: true };
  saveFeatureFlags(updatedFlags);
}

/**
 * Disable a specific feature flag
 */
export function disableFeature(flag: keyof FeatureFlagsType): void {
  const currentFlags = loadFeatureFlags();
  const updatedFlags = { ...currentFlags, [flag]: false };
  saveFeatureFlags(updatedFlags);
}

/**
 * Toggle a feature flag
 */
export function toggleFeature(flag: keyof FeatureFlagsType): boolean {
  const currentFlags = loadFeatureFlags();
  const newValue = !currentFlags[flag];
  const updatedFlags = { ...currentFlags, [flag]: newValue };
  saveFeatureFlags(updatedFlags);
  return newValue;
}

/**
 * Reset all feature flags to epic-based defaults
 */
export function resetFeatureFlags(): void {
  const epicProgress = getEpicProgress();
  const resetFlags = {
    ...DEFAULT_FEATURE_FLAGS,
    ...getEpicBasedFlags(epicProgress)
  };
  saveFeatureFlags(resetFlags);
}

/**
 * Get all enabled features for current epic
 */
export function getEnabledFeatures(): string[] {
  const flags = loadFeatureFlags();
  return Object.entries(flags)
    .filter(([_, enabled]) => enabled)
    .map(([flag, _]) => flag);
}

/**
 * Get features available for a specific epic
 */
export function getFeaturesForEpic(epic: number): string[] {
  const epicFlags = EPIC_FEATURE_FLAGS[epic] || {};
  return Object.keys(epicFlags);
}

/**
 * Check if user has access to epic-specific features
 */
export function hasEpicAccess(epic: number): boolean {
  return getEpicProgress() >= epic;
}

/**
 * Development/Demo Helper: Simulate epic completion
 */
export function simulateEpicCompletion(epic: number): void {
  if (import.meta.env.MODE !== 'production') {
    updateEpicProgress(epic);
    console.log(`🎯 Simulated Epic ${epic} completion - features enabled:`, getFeaturesForEpic(epic));
  }
}

/**
 * Development/Demo Helper: Enable demo mode with all features
 */
export function enableDemoMode(): void {
  if (import.meta.env.MODE !== 'production') {
    const allEnabledFlags: FeatureFlagsType = Object.keys(DEFAULT_FEATURE_FLAGS).reduce(
      (flags, key) => ({ ...flags, [key]: true }),
      {} as FeatureFlagsType
    );
    saveFeatureFlags(allEnabledFlags);
    console.log('🎬 Demo mode enabled - all features accessible');
  }
}

/**
 * Development/Demo Helper: Disable demo mode
 */
export function disableDemoMode(): void {
  if (import.meta.env.MODE !== 'production') {
    resetFeatureFlags();
    console.log('🔒 Demo mode disabled - returned to epic-based access');
  }
}

// Legacy compatibility exports
export const FeatureFlags: Record<string, boolean> = loadFeatureFlags() as unknown as Record<string, boolean>;
export const checkFeatureImplemented = isFeatureEnabled;
export const updateFeatureFlag = (feature: string, enabled: boolean) => {
  if (enabled) enableFeature(feature as keyof FeatureFlagsType);
  else disableFeature(feature as keyof FeatureFlagsType);
};

// Export feature flag utilities
export const FeatureFlagUtils = {
  load: loadFeatureFlags,
  save: saveFeatureFlags,
  isEnabled: isFeatureEnabled,
  enable: enableFeature,
  disable: disableFeature,
  toggle: toggleFeature,
  reset: resetFeatureFlags,
  getEnabled: getEnabledFeatures,
  getFeaturesForEpic,
  hasEpicAccess,
  
  // Epic management
  getEpicProgress,
  updateEpicProgress,
  
  // Demo/Development helpers
  simulateEpicCompletion,
  enableDemoMode,
  disableDemoMode
};

// Initialize feature flags on module load
loadFeatureFlags();
